import { ComponentMatrixGroup, HeaderCellModel } from '../../types';
import { COMPONENT_HIERARCHY_PADDING } from '../../consts';

type RowHeaderModel = { headerCells: HeaderCellModel[] };

type ReducerState = {
  rows: RowHeaderModel[];
  // HeaderCells of expanded ancestor groups whose row hasn't been emitted yet.
  // Flushed onto the rowModel of the next leaf descendant in DFS order, so an
  // ancestor's headerCell ends up sharing its `rowIndex` with its first leaf.
  unflushedAncestors: HeaderCellModel[];
  parentIndexAddress: number[];
};

const getRowHeaderModels = (
  rows: Map<string | null, ComponentMatrixGroup>,
  leafCounts: Map<ComponentMatrixGroup, number>
): RowHeaderModel[] => {
  const reducer = (
    state: ReducerState,
    currentRow: [string | null, ComponentMatrixGroup],
    currentRowIndex: number,
    currentEntries: [string | null, ComponentMatrixGroup][]
  ): ReducerState => {
    const [currentKey, currentGroup] = currentRow;
    const indexAddressKey =
      currentKey === COMPONENT_HIERARCHY_PADDING ? NaN : currentRowIndex;
    const indexAddress = [...state.parentIndexAddress, indexAddressKey];

    // Only recurse into children when the group is expanded. A collapsed group
    // is treated as a single visible leaf row — mirroring the leafCounts map and
    // the column-side buildColumnsByDepthWithGridInfo behaviour. PADDING groups
    // are always expanded, so they continue to recurse correctly.
    //
    // Ancestor headerCells are buffered in `pendingHeaderCells` and flushed onto
    // the *first leaf descendant* in DFS order (see the leaf branch below).
    // Consequently, an ancestor's headerCell shares its rowModel — and therefore
    // its `rowIndex` — with that first leaf. Consumers rely on this invariant:
    // `gridRow: ${rowIndex + 1} / ${rowIndex + 1 + headerCell.span}` in
    // componentMatrixRowHeaderCell, and `rowHeights[rowIndex]` as the first
    // sub-row's track height in componentMatrixRowHeader.
    if (currentGroup.children.size && currentGroup.isExpanded) {
      state.unflushedAncestors.push({
        value: currentKey,
        span: [...currentGroup.children.values()].reduce(
          (acc, child) => acc + (leafCounts.get(child) ?? 1),
          0
        ),
        group: currentGroup,
        indexAddress,
        siblingCount: currentEntries.length,
      });
      const parentIndexAddress = state.parentIndexAddress;
      const newState = [...currentGroup.children.entries()].reduce(reducer, {
        ...state,
        parentIndexAddress: indexAddress,
      });
      newState.parentIndexAddress = parentIndexAddress;
      return newState;
    }

    state.rows.push({
      headerCells: [
        ...state.unflushedAncestors,
        {
          value: currentKey,
          span: 1,
          group: currentGroup,
          indexAddress,
          siblingCount: currentEntries.length,
        },
      ],
    });

    if (state.unflushedAncestors.length) {
      state.unflushedAncestors = [];
    }

    return state;
  };

  return [...rows.entries()].reduce(reducer, {
    rows: [],
    unflushedAncestors: [],
    parentIndexAddress: [],
  }).rows;
};

export { getRowHeaderModels };
